import { Box, Typography, alpha, useTheme } from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { ACCENT } from "./accent";

// ArduCopter flight-mode numbers. Only applied when the log is a copter — other
// vehicles number their modes differently, so we fall back to "Mode N".
const COPTER_MODES = {
  0: "Stabilize", 1: "Acro", 2: "AltHold", 3: "Auto", 4: "Guided", 5: "Loiter",
  6: "RTL", 7: "Circle", 9: "Land", 11: "Drift", 13: "Sport", 14: "Flip",
  15: "AutoTune", 16: "PosHold", 17: "Brake", 18: "Throw", 20: "Guided_NoGPS",
  21: "Smart_RTL", 22: "FlowHold", 23: "Follow", 24: "ZigZag",
};

const usToClock = (us, baseUs) => {
  if (us == null || baseUs == null) return "";
  const total = Math.max(0, Math.round((us - baseUs) / 1e6));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `+${m}:${String(sec).padStart(2, "0")}`;
};

const modeLabel = (mode, vehicle) => {
  if (vehicle === "ArduCopter" && COPTER_MODES[mode]) return COPTER_MODES[mode];
  return `Mode ${mode}`;
};

const LogTimeline = ({ log }) => {
  const theme = useTheme();
  const s = log?.summary || {};

  const entries = [];
  for (const m of s.modes || []) entries.push({ kind: "mode", t_us: m.t_us, text: modeLabel(m.mode, log?.vehicle_type) });
  for (const e of s.errors || []) entries.push({ kind: "error", t_us: e.t_us, text: `Error — subsystem ${e.subsys}, code ${e.ecode}` });
  for (const e of s.events || []) entries.push({ kind: "event", t_us: e.t_us, text: `Event ${e.id}` });

  const timed = entries.filter((e) => e.t_us != null).sort((a, b) => a.t_us - b.t_us);
  if (timed.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        No mode changes or events recorded.
      </Typography>
    );
  }
  const baseUs = timed[0].t_us;

  const iconFor = (kind) => {
    if (kind === "error") return <ErrorOutlineOutlinedIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />;
    if (kind === "event") return <BoltOutlinedIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />;
    return <FlagOutlinedIcon sx={{ fontSize: 18, color: ACCENT }} />;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {timed.map((e, i) => (
        <Box
          key={i}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1.5,
            backgroundColor:
              e.kind === "error" ? alpha(theme.palette.error.main, 0.08) : "transparent",
          }}
        >
          <Typography
            variant="caption"
            sx={{ minWidth: 48, fontFamily: "monospace", color: theme.palette.text.secondary }}
          >
            {usToClock(e.t_us, baseUs)}
          </Typography>
          {iconFor(e.kind)}
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            {e.text}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default LogTimeline;
